import type { Mode, Profanity, AspectRatio } from "@/types/roast";
import type { CaptionMask, BleepMode } from "@/config/censorship";
import type { GenerationResult } from "./runGeneration";

export type DetectedSourceType = "deck" | "doc" | "photo";

/** Everything the pipeline needs, collected by the Create screen. */
export interface CreateRequest {
  source: File;
  detectedType: DetectedSourceType;
  mode: Mode;                 // slider side (non-null; Generate requires off-center)
  profanity: Profanity;       // "medium" | "savage"
  doodle: boolean;            // default true; applied per detectedType downstream
  pageCount: number;          // 0 for photo; from validatePdf for PDFs
  voiceId: string;            // DEFAULT_VOICE[mode]
  roastId: string;
  aspect: AspectRatio;        // photo/doc "9:16", deck "16:9"
  coverBlob: Blob;            // rendered cover — carried so /processing shows the same thumbnail
  name: string | null;        // classic StudioCreate collects these; the redesigned CreateScreen passes null
  context: string | null;
  length: "microwave" | "deepFry";
  // Opt-in censorship flags (omitted ⇒ today's behavior — see @/config/censorship).
  captionMask?: CaptionMask;  // caption redaction style; default "vowels"
  bleepMode?: BleepMode;      // spoken-audio mode; default "tone" (real word bleeped)
}

import { saveResultCache, clearResultCache, saveRequestCache, clearRequestCache } from "./resultCache";

// In-memory store (a File/Blobs can't ride a URL param), MIRRORED to device-local
// IndexedDB slots so a refresh on /player can restore the last generation AND the
// request that produced it (Flip keeps working). Fire-and-forget: cache failures
// never affect the in-memory flow.
let _pending: CreateRequest | null = null;
let _result: GenerationResult | null = null;
export const createFlow = {
  set(req: CreateRequest) { _pending = req; void saveRequestCache(req); },
  get(): CreateRequest | null { return _pending; },
  setResult(r: GenerationResult | null) {
    _result = r;
    if (r) void saveResultCache(r); else void clearResultCache();
  },
  getResult(): GenerationResult | null { return _result; },
  clear() { _pending = null; _result = null; void clearResultCache(); void clearRequestCache(); },
};
